import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UpdoxDirectMessage, ComposeDraft } from '../updox-direct-message';
import { UpdoxMessagesService } from '../updox-messages.service';
import { AlertService } from '../../../../_alert';
import { GlobalErrorHandlerService } from '../../../../services/global-error.service';

@Component({
  selector: 'app-updox-message-detail',
  templateUrl: './message-detail.component.html',
})
export class UpdoxMessageDetailComponent implements OnChanges {
  @Input() message: UpdoxDirectMessage;
  @Output() reply = new EventEmitter<ComposeDraft>();
  @Output() editDraft = new EventEmitter<UpdoxDirectMessage>();
  @Output() deleted = new EventEmitter<UpdoxDirectMessage>();

  isDeleting = false;
  showDeleteConfirm = false;
  isMarkingUnread = false;
  showAttachments = false;
  isLoadingBody = false;
  fullMessage: UpdoxDirectMessage | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.message) {
      this.showAttachments   = false;
      this.showDeleteConfirm = false;
      this.fullMessage       = null;

      if (this.message) {
        this.isLoadingBody = true;
        this.messagesService.getMessage(this.message.id)
          .then(json => {
            this.isLoadingBody = false;
            this.fullMessage = new UpdoxDirectMessage().load_from_json(json);
          })
          .catch(() => {
            this.isLoadingBody = false;
            this.fullMessage = this.message;
          });
      }
    }
  }

  get isDraft(): boolean {
    return this.message?.folder === 'draft';
  }

  get isInbound(): boolean {
    return this.message?.direction === 'inbound';
  }

  get deleteConfirmText(): string {
    if (this.message?.folder === 'deleted') {
      return 'This will permanently remove the message and cannot be undone.';
    }
    if (this.message?.folder === 'draft') {
      return 'This draft will be permanently deleted.';
    }
    return 'This message will be moved to Trash. You can find it in the Deleted folder.';
  }

  constructor(
    private messagesService: UpdoxMessagesService,
    private alertService: AlertService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {}

  replyToMessage(): void {
    const original = this.fullMessage || this.message;
    const quotedDate = original.created_at
      ? new Date(original.created_at).toLocaleString()
      : '';
    const quotedBody = [
      '',
      '',
      `--- Original Message ---`,
      `From: ${original.from_address}`,
      quotedDate ? `Date: ${quotedDate}` : '',
      `Subject: ${original.subject}`,
      '',
      original.body_text || '',
    ].filter((line, i) => i < 4 || line !== '').join('\n');

    const draft: ComposeDraft = {
      localId:             Date.now(),
      to_address:          original.from_address,
      subject:             original.subject.startsWith('Re:')
                             ? original.subject
                             : `Re: ${original.subject}`,
      body_text:           quotedBody,
      reply_to_message_id: original.id,
      minimized:           false,
      replyTo:             original,
    };
    this.reply.emit(draft);
  }

  markUnread(): void {
    if (this.isMarkingUnread) { return; }
    this.isMarkingUnread = true;
    this.messagesService.markRead(this.message.id, false)
      .then(() => {
        this.isMarkingUnread = false;
        this.message.read = false;
      })
      .catch(() => {
        this.isMarkingUnread = false;
        this.globalErrorHandler.error = 'Failed to mark message as unread.';
      });
  }

  confirmDelete(): void {
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
  }

  deleteMessage(): void {
    this.isDeleting = true;
    this.showDeleteConfirm = false;
    this.messagesService.deleteMessage(this.message.id)
      .then((resp: any) => {
        this.isDeleting = false;
        const toast = resp?.permanently_deleted
          ? 'Message permanently deleted.'
          : 'Message moved to Trash.';
        this.alertService.success(toast, this.alertService.options);
        this.deleted.emit(this.message);
      })
      .catch(() => {
        this.isDeleting = false;
        this.globalErrorHandler.error = 'Failed to delete message. Please try again.';
      });
  }
}
